import { useState } from 'react'
import { Alert, Badge, Card, Group, Modal, Select, Stack, Text, Title } from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconClockHour4, IconHistory, IconInbox } from '@tabler/icons-react'
import {
  ETIQUETA_TIPO, TIPOS_DOCUMENTO,
  type DocumentoPendiente, type Estado, type TipoDocumento,
} from '@serkat/shared'
import { ErrorApi, api, queryString } from '../../api/cliente'
import { useSesion } from '../auth/useSesion'
import { AccionesEstado } from '../../compartido/componentes/AccionesEstado'
import { HistorialEstados } from '../../compartido/componentes/HistorialEstados'
import { fecha, moneda } from '../../compartido/formato'

export function ValidacionesPagina() {
  const { usuario } = useSesion()
  const qc = useQueryClient()
  const [tipo, setTipo] = useState<string | null>(null)
  const [verHistorial, setVerHistorial] = useState<DocumentoPendiente | null>(null)

  const { data: pendientes, isPending } = useQuery({
    queryKey: ['validaciones', tipo],
    queryFn: () =>
      api.get<DocumentoPendiente[]>(`/validaciones/pendientes${queryString({ tipo: tipo ?? undefined })}`),
  })

  const transicion = useMutation({
    mutationFn: ({ id, estado, motivo }: { id: string; estado: Estado; motivo: string | null }) =>
      api.post(`/documentos/${id}/transicion`, motivo ? { estado, motivo } : { estado }),
    onSuccess: (_d, v) => {
      notifications.show({
        color: v.estado === 'Aprobado' ? 'teal' : v.estado === 'Anulado' ? 'red' : 'blue',
        message: `Documento ${v.estado.toLowerCase()}`,
      })
      void qc.invalidateQueries({ queryKey: ['validaciones'] })
      void qc.invalidateQueries({ queryKey: ['requerimientos'] })
      void qc.invalidateQueries({ queryKey: ['ordenes'] })
    },
    onError: (e) =>
      notifications.show({
        color: 'red',
        title: 'No se pudo completar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  // El permiso es POR TIPO desde la migración 024: Procura decide
  // requerimientos y nada más. `puedeTransicionar` solo dice si decide sobre
  // algo, y sirve para el aviso de abajo; para pintar los botones hay que
  // mirar el tipo de cada documento, o se ofrecería una acción que la base va
  // a rechazar y el usuario no sabría si es permiso o avería.
  const decideAlgo = usuario?.puedeTransicionar ?? false
  const decide = (t: TipoDocumento) => usuario?.tiposQueDecide.includes(t) ?? false

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Validaciones</Title>
        <Select
          placeholder="Todos los formatos"
          data={TIPOS_DOCUMENTO.map((t) => ({ value: t, label: ETIQUETA_TIPO[t as TipoDocumento] }))}
          value={tipo}
          onChange={setTipo}
          clearable
          w={230}
        />
      </Group>

      {!decideAlgo && (
        <Alert color="blue" variant="light">
          Su rol no aprueba documentos. Puede ver la bandeja y el historial, pero las
          decisiones las toman Procura, Dirección o Gerencia, según el documento.
        </Alert>
      )}

      {!isPending && (pendientes?.length ?? 0) === 0 && (
        <Card withBorder padding="xl">
          <Stack align="center" gap="xs">
            <IconInbox size={32} opacity={0.4} />
            <Text c="dimmed">No hay documentos esperando decisión.</Text>
          </Stack>
        </Card>
      )}

      <Stack gap="sm">
        {(pendientes ?? []).map((d) => (
          <Card key={d.id} withBorder padding="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={6} style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text ff="monospace" fw={600}>{d.numero}</Text>
                  <Badge size="sm" variant="light">
                    {ETIQUETA_TIPO[d.tipo]}
                    {d.subtipo !== '-' && ` · ${d.subtipo.toLowerCase()}`}
                  </Badge>
                  {d.diasEnEspera >= 3 && (
                    <Badge size="sm" color="orange" variant="light"
                      leftSection={<IconClockHour4 size={12} />}>
                      {d.diasEnEspera} días en espera
                    </Badge>
                  )}
                </Group>

                <Text size="sm">
                  {d.empresa}
                  {d.proveedor && <> · Proveedor: <strong>{d.proveedor}</strong></>}
                  {d.total !== null && (
                    <> · <span style={{ fontFamily: 'monospace' }}>
                      {moneda(d.total, (d.moneda as 'PEN' | 'USD') ?? 'PEN')}
                    </span></>
                  )}
                </Text>

                <Text size="xs" c="dimmed">
                  Emitido el {fecha(d.fechaEmision)}
                  {d.emitidoPor && ` por ${d.emitidoPor}`}
                  {d.obra && ` · Obra: ${d.obra}`}
                  {d.area && ` · Área: ${d.area}`}
                </Text>

                <Group gap={6} mt={4}>
                  <Text
                    size="xs" c="dimmed" style={{ cursor: 'pointer' }}
                    onClick={() => setVerHistorial(d)}
                  >
                    <IconHistory size={12} style={{ verticalAlign: -2 }} /> Ver historial
                  </Text>
                </Group>
              </Stack>

              {decide(d.tipo) && (
                <AccionesEstado
                  estado={d.estado}
                  ocupado={transicion.isPending}
                  onTransicionar={(estado, motivo) => transicion.mutate({ id: d.id, estado, motivo })}
                />
              )}
            </Group>
          </Card>
        ))}
      </Stack>

      <Modal
        opened={verHistorial !== null}
        onClose={() => setVerHistorial(null)}
        title={`Historial de ${verHistorial?.numero ?? ''}`}
        size="lg"
      >
        {verHistorial && <HistorialEstados documentoId={verHistorial.id} />}
      </Modal>
    </Stack>
  )
}
